import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Heart, ArrowRight } from "lucide-react";
import confetti from "canvas-confetti";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { goalPct, money } from "@/lib/tipgoal";

function fireConfetti() {
  const colors = ["#34d399", "#fbbf24", "#a78bfa"];
  confetti({ particleCount: 90, spread: 70, origin: { y: 0.4 }, colors });
}

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [tipInfo, setTipInfo] = useState(null);
  const attempts = useRef(0);
  const celebrated = useRef(false);

  const isCancel = location.pathname.includes("cancel");

  useEffect(() => {
    if (isCancel) { setStatus("cancel"); return; }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          if (data.type === "tip" && data.artist_id) {
            try {
              const { data: card } = await api.get(`/tip-card/${data.artist_id}`);
              setTipInfo(card);
            } catch {}
          } else {
            await refreshUser();
          }
          if (!celebrated.current) { celebrated.current = true; fireConfetti(); }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch {}
      if (attempts.current >= 10) { setStatus("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [location, isCancel, refreshUser]);

  // Thank-you moment for tips
  if (status === "success" && tipInfo) {
    const pct = goalPct(tipInfo.tip_progress_cents, tipInfo.tip_goal_cents);
    return (
      <div className="min-h-screen bg-[#0A0A0E] text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md w-full nu-card p-8" data-testid="tip-thankyou">
          <div className="flex justify-center mb-4"><span className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center"><Heart className="w-8 h-8 text-emerald-400" /></span></div>
          <h1 className="font-display font-extrabold text-2xl">Thank you! 🎉</h1>
          <p className="mt-2 text-zinc-400">Your tip to <span className="text-white font-semibold">{tipInfo.artist_name}</span> came through.</p>

          {tipInfo.tip_goal_cents > 0 && (
            <div className="mt-6 text-left">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-zinc-300 font-semibold">{tipInfo.tip_goal_label || "Tip goal"}</span>
                <span className="font-mono-nu text-zinc-400">{money(tipInfo.tip_progress_cents)} / {money(tipInfo.tip_goal_cents)}</span>
              </div>
              <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                <div className={`h-full rounded-full ${pct >= 100 ? "bg-amber-400" : "bg-gradient-to-r from-emerald-500 to-emerald-400"}`} style={{ width: `${pct}%` }} data-testid="thankyou-progress" />
              </div>
              <p className="mt-2 text-sm text-emerald-400 text-center">You helped push the goal to {pct}%!</p>
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3">
            {user ? (
              <button data-testid="thankyou-profile-btn" onClick={() => navigate(`/artist/${tipInfo.id}`)}
                className="w-full py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors flex items-center justify-center gap-2">
                See more from {tipInfo.artist_name} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button data-testid="thankyou-join-btn" onClick={() => navigate("/auth")}
                className="w-full py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors flex items-center justify-center gap-2">
                Join NEXT UP to follow {tipInfo.artist_name} <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => navigate(`/tip/${tipInfo.id}`)} className="text-sm text-zinc-400 hover:text-white transition-colors">Back to {tipInfo.artist_name}'s page</button>
          </div>
        </div>
      </div>
    );
  }

  const ICONS = {
    checking: <Loader2 className="w-14 h-14 text-emerald-400 animate-spin" />,
    success: <CheckCircle className="w-14 h-14 text-emerald-400" />,
    failed: <XCircle className="w-14 h-14 text-red-500" />,
    cancel: <XCircle className="w-14 h-14 text-amber-400" />,
  };
  const TEXT = {
    checking: { t: "Confirming your payment…", s: "Hang tight, this only takes a moment." },
    success: { t: "Payment successful 🎉", s: "All done — your transaction went through." },
    failed: { t: "Payment not completed", s: "Something went wrong. You can try again anytime." },
    cancel: { t: "Checkout cancelled", s: "No charge was made. You can subscribe whenever you're ready." },
  };

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white flex items-center justify-center p-6">
      <div className="text-center max-w-md" data-testid="payment-result">
        <div className="flex justify-center mb-6">{ICONS[status]}</div>
        <h1 className="font-display font-extrabold text-2xl">{TEXT[status].t}</h1>
        <p className="mt-2 text-zinc-400">{TEXT[status].s}</p>
        {status !== "checking" && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <button data-testid="payment-continue-btn" onClick={() => navigate(status === "success" ? "/feed" : "/subscribe")}
              className="px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors">
              {status === "success" ? "Enter NEXT UP" : "Back to Subscribe"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
